import { useEffect } from "react";
import {
  Form,
  Link,
  Navigate,
  useLoaderData,
  useActionData,
  useNavigation,
} from "react-router-dom";
import toast from "react-hot-toast";
import { Header } from "../../components/layout/Header";
import { UpgradePrompt, Button, LinkButton } from "../../components/common";
import type { CreateTeamData } from "../../routes/loaders";
import type { ActionResult } from "../../routes/actions";
import "./CreateTeamPage.css";

export default function CreateTeamPage() {
  const { organizationId, teams, permissions } = useLoaderData() as CreateTeamData;
  const actionData = useActionData() as ActionResult | undefined;
  const navigation = useNavigation();

  const isSubmitting = navigation.state === "submitting";

  useEffect(() => {
    if (actionData?.error) {
      toast.error(actionData.error);
    }
  }, [actionData]);

  if (!organizationId) {
    return <Navigate to="/organizations" replace />;
  }

  // Use server-computed permissions for access control
  if (!permissions.canManageMembers) {
    return <Navigate to={`/organizations/${organizationId}/teams`} replace />;
  }

  return (
    <>
      <Header />
      <div className="container">
        <div className="create-team-container">
          <div className="create-team-back">
            <Link to={`/organizations/${organizationId}/teams`} className="back-link">
              ← Back to Teams
            </Link>
          </div>

          <div className="create-team-card">
            <h1>Create Team</h1>
            <p className="create-team-description">Create a new team within this organization.</p>

            {!permissions.canCreateTeam ? (
              <UpgradePrompt
                organizationId={organizationId}
                title={teams.length === 0 ? "Teams Not Available" : "Team Limit Reached"}
                message={
                  teams.length === 0
                    ? "The Free tier does not include teams. Upgrade to Pro to create and manage teams."
                    : "Your organization has reached its team limit. Upgrade to Pro to create more teams."
                }
              />
            ) : (
              <Form method="post" className="create-team-form">
                <div className={`form-group ${actionData?.fieldErrors?.name ? "error" : ""}`}>
                  <label htmlFor="name">Team Name *</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Engineering Team"
                    disabled={isSubmitting}
                  />
                  {actionData?.fieldErrors?.name && (
                    <span className="form-error">{actionData.fieldErrors.name}</span>
                  )}
                </div>

                <div
                  className={`form-group ${actionData?.fieldErrors?.description ? "error" : ""}`}
                >
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    name="description"
                    placeholder="A team for software engineers working on core products..."
                    rows={4}
                    disabled={isSubmitting}
                  />
                  {actionData?.fieldErrors?.description && (
                    <span className="form-error">{actionData.fieldErrors.description}</span>
                  )}
                </div>

                <div className="form-actions">
                  <Button
                    type="submit"
                    variant="primary"
                    loading={isSubmitting}
                    loadingText="Creating..."
                  >
                    Create Team
                  </Button>
                  <LinkButton to={`/organizations/${organizationId}/teams`} variant="secondary">
                    Cancel
                  </LinkButton>
                </div>
              </Form>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
